import i18n from 'i18next';
import { Component, type ReactNode } from 'react';

interface State { hasError: boolean; detail: string }

/**
 * App-level error boundary. Converts a render crash from a blank white screen into a recoverable
 * message, and logs the error + component stack to the console so the underlying bug can be
 * captured (`[Agavi] Uncaught render error: …`).
 */
export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { hasError: false, detail: '' };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    return { hasError: true, detail: message };
  }

  override componentDidCatch(error: unknown, info: { componentStack?: string }) {
    console.error('[Agavi] Uncaught render error:', error, info?.componentStack);
    /*
     * THE COMPONENT STACK IS THE HALF THAT NAMES THE BUG, and it only ever reached the console.
     *
     * "This page hit an unexpected error" is the whole of what a teacher can report, so a crash
     * they hit while navigating arrives as a description of the screen rather than of the fault
     * — and the console they would have to open has usually been cleared by the reload the
     * message recommends. Held on the instance rather than in state: it is read once, by the
     * copy button, and putting it in state would re-render the fallback for nothing.
     */
    this.stack = (info?.componentStack ?? '').trim();
  }

  /** Set by `componentDidCatch`, read by the copy button below. */
  private stack = '';

  override render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className='flex h-screen flex-col items-center justify-center gap-4 bg-ws-bg p-6 text-center'>
        <h1 className='text-lg font-bold text-ws-text'>{i18n.t('errors.boundaryTitle')}</h1>
        <p className='max-w-md text-sm text-ws-muted'>{i18n.t('errors.boundaryBody')}</p>
        {/* THE FAULT ITSELF, on screen. Small and muted — it is not for the teacher to read,
            it is for them to be able to SEND, which is exactly what the sentence above asks
            them to do and gave them nothing to do it with. */}
        {this.state.detail && (
          <p className='max-w-md select-text break-words font-mono text-[11px] leading-tight text-ws-muted/80'>
            {this.state.detail}
          </p>
        )}
        <div className='flex items-center gap-2'>
          <button
            onClick={() => window.location.reload()}
            className='rounded-lg bg-ws-primary px-5 py-2 text-sm font-medium text-white hover:opacity-90'
          >
            {i18n.t('errors.boundaryReload')}
          </button>
          {this.state.detail && (
            <button
              onClick={() => {
                void navigator.clipboard?.writeText(
                  [this.state.detail, this.stack, window.location.href].filter(Boolean).join('\n\n'));
              }}
              className='rounded-lg border border-ws-border px-4 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover'
            >
              {i18n.t('errors.boundaryCopy')}
            </button>
          )}
        </div>
      </div>
    );
  }
}
